import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import SimLockOverlay from "./components/SimLockOverlay";

const API_BASE = (import.meta.env.VITE_API_BASE as string) || "/api";

/**
 * 客户权限状态，由线上现有 /user/sim-status 接口返回。
 * addonActive=true 代表管理端 Dashboard 已经给客户开通有效 Addon。
 */
interface SimStatus {
  userId: number;
  hasProfile: boolean;
  simEnabled: boolean;
  addonActive: boolean;
  addonExpired: boolean;
}

/**
 * 需要客服审核开通的核心功能路由。
 * 当前线上客户 Dashboard 首页（/）和个人中心不在此列表中，
 * 因此未开通客户登录后可以先浏览原有 Dashboard，不会立即被弹层遮挡。
 */
const CORE_FEATURE_ROUTES = [
  "/watchlist",    // AI 自选 / AI 选股
  "/positions",    // 模拟盘持仓
  "/stock",        // 个股 AI 分析
  "/decision",     // AI 决策详情
  "/ai-trader",    // AI Trader / AI 助手
  "/ai-strategy",  // AI 策略、创建、编辑和回测
  "/research",     // 研报工作台
  "/reports",      // 模拟盘资产报告
] as const;

function isCoreFeatureRoute(pathname: string): boolean {
  return CORE_FEATURE_ROUTES.some((route) =>
    pathname === route || pathname.startsWith(`${route}/`),
  );
}

/** 查询当前登录客户的核心功能开通状态。 */
async function checkSimStatus(): Promise<SimStatus | null> {
  const token = localStorage.getItem("alpha_token") || localStorage.getItem("token");
  if (!token) return null;
  try {
    const r = await fetch(`${API_BASE}/user/sim-status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    const j = await r.json();
    if (!j.success) return null;
    return j.data;
  } catch {
    return null;
  }
}

function SimGuard({ children }: { children: React.ReactNode }) {
  const [locked, setLocked] = useState(false);
  const [userId, setUserId] = useState(0);
  const [addonExpired, setAddonExpired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lockedRef = useRef(false);

  const doCheck = useCallback(async () => {
    const status = await checkSimStatus();
    if (status && status.userId) setUserId(status.userId);
    setAddonExpired(Boolean(status?.addonExpired));

    // 核心修改：不再根据“已填写投资偏好”全站强制弹出企业微信。
    // 只有未开通 Addon 的客户进入核心功能路由时才显示原客服弹层。
    const shouldLock = Boolean(
      status && isCoreFeatureRoute(location.pathname) && !status.addonActive,
    );
    lockedRef.current = shouldLock;
    setLocked(shouldLock);
  }, [location.pathname]);

  // 首次进入和每次切换页面时重新查询，管理端开通后刷新即可生效。
  useEffect(() => { doCheck(); }, [doCheck, location.pathname]);

  // API 客户端遇到 alpha_subscription_required 时会发送 sim-lock 事件。
  // 必须再次核对当前路由：Dashboard 自身接口即使返回 403，也不能把首页遮住；
  // 只有客户实际点击并进入核心功能后才显示企业微信弹层。
  useEffect(() => {
    const handler = () => {
      if (isCoreFeatureRoute(location.pathname) && !lockedRef.current) {
        lockedRef.current = true;
        setLocked(true);
      }
    };
    window.addEventListener("sim-lock", handler);
    return () => window.removeEventListener("sim-lock", handler);
  }, [location.pathname]);

  // 允许客户从核心功能提示层返回当前线上 Dashboard 继续浏览。
  const backToDashboard = () => {
    lockedRef.current = false;
    setLocked(false);
    navigate("/");
  };

  return (
    <>
      {children}
      {locked && (
        <SimLockOverlay
          userId={userId}
          addonExpired={addonExpired}
          onBack={backToDashboard}
        />
      )}
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <SimGuard>
          <AppRoutes />
        </SimGuard>
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
